import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { useInviteEmployees } from '@/modules/common/hooks/useEmployer';
import { ApiErrorResponse } from '@/types/api';
import { CloudUpload, Refresh } from '@mui/icons-material';
import { Button } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

type UploadEmployeesDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle' | 'actions'
> & {
  company: string;
  onSuccess?: () => void;
};

export function UploadEmployeesDialog({
  company,
  onSuccess,
  onClose,
  ...props
}: UploadEmployeesDialog) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { file: null },
  });

  const [selectedFileName, setSelectedFileName] = useState('Choose csv file');

  const { mutateAsync: inviteEmployees, isPending: isUploadingFile } =
    useInviteEmployees();

  const handleUploadSubmit = async (data: { file: FileList | null }) => {
    if (!data.file) return;

    const file = data.file[0];

    const formData = new FormData();
    formData.append('file', file);

    try {
      await inviteEmployees({
        id: company,
        file: formData,
      });
      onSuccess?.();
      enqueueSnackbar(`File uploaded successfully`, {
        variant: 'success',
      });
      reset();
    } catch (error) {
      if ((error as ApiErrorResponse).statusCode === 400) {
        return setError('root.serverError', {
          type: 'manual',
          message:
            (error as ApiErrorResponse).message ??
            'An error occurred. Please try again later.',
        });
      }
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      {...props}
      title="Upload Employees"
      subTitle="Upload a CSV file to invite multiple employees at once"
      onClose={(event, reason) => onClose?.(event, reason)}
      classes={{
        paper: 'max-w-xl mx-auto p-8 pb-3',
      }}
      actions={[
        <Button
          key="cancel"
          variant="outlined"
          onClick={() => onClose?.({}, 'backdropClick')}
          disabled={isUploadingFile}
        >
          Cancel
        </Button>,
        <Button
          key="upload"
          variant="contained"
          type="submit"
          form="upload-employees-form"
          loading={isUploadingFile}
        >
          Upload
        </Button>,
      ]}
    >
      <form
        id="upload-employees-form"
        onSubmit={handleSubmit(handleUploadSubmit)}
        className="flex flex-col justify-end gap-3"
      >
        {errors.root?.serverError?.message && (
          <ul className="text-red-500 list-disc">
            {errors.root.serverError.message?.split(';').map((error, index) => (
              <li key={index}>
                {error}
                <br />
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col gap-6">
          <Controller
            control={control}
            name="file"
            render={({ field: { onChange, value, ...field } }) => (
              <>
                <p>
                  Upload a CSV file with your employees info and have them
                  invited all at once.
                </p>
                <p>Here's an example of the CSV file format we're expecting:</p>
                <p className="text-gray-500 pl-4 pr-4 pt-2 pb-2 border rounded text-sm">
                  <code className="block">name,role,team</code>
                  <code className="block">
                    user1@mycompany.com,admin,Engineering
                  </code>
                  <code className="block">
                    user2@mycompany.com,user,Marketing
                  </code>
                </p>
                <label
                  htmlFor="csv-file-upload"
                  className="px-3 py-2 border rounded-lg cursor-pointer shrink-0 active:bg-mydra-gray text-primary flex items-center justify-between gap-2"
                >
                  {isUploadingFile ? (
                    <Refresh className="animate-spin" />
                  ) : (
                    <>
                      <span>{selectedFileName}</span>
                      <CloudUpload className="w-5 h-5 text-primary" />
                    </>
                  )}
                </label>
                <input
                  {...field}
                  id="csv-file-upload"
                  type="file"
                  accept=".csv"
                  onChange={e => {
                    onChange(e.target.files);
                    setSelectedFileName(
                      e.target.files?.[0]?.name || 'Choose csv file',
                    );
                  }}
                  className="invisible w-0 h-0"
                  disabled={isUploadingFile}
                />
              </>
            )}
          />
          {errors.file?.message && (
            <p className="text-sm text-red-500">{errors.file.message}</p>
          )}
        </div>
      </form>
    </Modal>
  );
}
